import React, { useRef, useEffect } from 'react';
import { ArrowRight, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(err => console.log('Video autoplay failed:', err));
    }
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-slate-950">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-50"
        >
          <source src="/video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-900/60 to-slate-950/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-8 perspective-1000"
          >
            <div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light text-white mb-6 leading-tight">
                Expert Drainage
                <span className="block font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                  Made Simple
                </span>
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed max-w-xl">
                Professional drainage repairs and maintenance with 25+ years of expertise. Fast, reliable, and built to last.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 rounded-2xl font-semibold text-lg group transition-all hover:scale-105 inline-flex items-center justify-center">
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/services" className="border border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-2xl font-semibold text-lg backdrop-blur-xl inline-flex items-center justify-center group hover:scale-105">
                Our Services
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10">
              {[
                { value: '25+', label: 'Years' },
                { value: '10K+', label: 'Projects' },
                { value: '98%', label: 'Satisfaction' }
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + idx * 0.05, duration: 0.2 }}
                >
                  <div className="text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Enhanced 3D Bento Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.2 }}
            className="hidden lg:grid grid-cols-2 gap-4"
            style={{ perspective: '1000px' }}
          >
            {/* Card 1 - Animated */}
            <motion.div
              initial={{ rotateY: -10 }}
              animate={{ rotateY: 0 }}
              whileHover={{ scale: 1.03, rotateY: 2, rotateX: 2, z: 20 }}
              transition={{ type: 'spring', stiffness: 600 }}
              className="col-span-2 bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 hover:bg-white/20 transition-all shadow-2xl"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <div className="flex items-center gap-4">
                <motion.div 
                  className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                >
                  <Wrench className="w-6 h-6 text-white" />
                </motion.div>
                <div>
                  <div className="text-white font-semibold">Professional Tools</div>
                  <div className="text-slate-300 text-sm">Advanced drainage equipment</div>
                </div>
              </div>
            </motion.div>

            {/* Card 2 - 3D Transform */}
            <motion.div
              initial={{ rotateY: -15 }}
              animate={{ rotateY: 0 }}
              whileHover={{ scale: 1.05, rotateY: -5, rotateX: 5, z: 30 }}
              transition={{ type: 'spring', stiffness: 600 }}
              className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-3 hover:bg-white/20 transition-all shadow-2xl"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <motion.div 
                className="text-3xl font-bold text-white mb-1"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                Fast
              </motion.div>
              <div className="text-xs text-slate-300">Response Time</div>
            </motion.div>

            {/* Card 3 - 3D Transform */}
            <motion.div
              initial={{ rotateY: 15 }}
              animate={{ rotateY: 0 }}
              whileHover={{ scale: 1.05, rotateY: 5, rotateX: -5, z: 30 }}
              transition={{ type: 'spring', stiffness: 600 }}
              className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-3 hover:bg-white/20 transition-all shadow-2xl"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <motion.div 
                className="text-3xl font-bold text-white mb-1"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.5 }}
              >
                Pro
              </motion.div>
              <div className="text-xs text-slate-300">Certified Team</div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Enhanced Animated gradient orbs with 3D effect */}
      <motion.div 
        className="absolute top-1/4 -right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, 30, 0]
        }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div 
        className="absolute bottom-1/4 -left-20 w-96 h-96 bg-slate-500/20 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.3, 1],
          x: [0, -50, 0],
          y: [0, -30, 0]
        }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div 
        className="absolute top-1/2 left-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.5, 1],
          rotate: [0, 180, 360]
        }}
        transition={{ duration: 7, repeat: Infinity, ease: 'linear' }}
      />
    </section>
  );
}